/**
 * Event Setup — the second tab, matching docs/prototype.html.
 *
 * Four panels: event details, what sponsors are told, logo specifications,
 * automated chaser. This IS the event edit form; the prototype has no separate
 * edit screen, and having one would mean two places to change the same fields.
 *
 * There was a fifth, for the event's own conference logo. It was removed on 17
 * September 2026: the artwork it collected never appeared in a sponsor email
 * or on the upload page, both of which lead with the client's brand, so the
 * panel was a file store with no reader and its own help text said so.
 *
 * Everything saves together. Splitting save per panel would mean four ways to
 * leave the event half-edited.
 */

import { useState, type FormEvent } from 'react';
import { ORIENTATIONS, TREATMENTS } from '../lib/variations';
import { T } from '../lib/copyEdit.tsx';

export interface EventData {
  id: string;
  name: string;
  status: 'draft' | 'active' | 'complete' | 'archived';
  startsOn: string | null;
  endsOn: string | null;
  deadline: { date: string; time: string; display: string | null; daysUntil: number | null } | null;
  notesForSponsors: string | null;
  maxFileSizeMb: number;
  minRasterWidthPx: number;
  graceWindowDays: number;
  variations: { id?: string; orientation: string; treatment: string }[];
  /**
   * Progress on this event, computed server-side.
   *
   * The server has always sent this and no client type declared it, so the
   * event detail page was rendering a header with no numbers in it while the
   * numbers sat unread in the payload. Every sponsor falls into exactly one of
   * approved, pendingReview or outstanding, so the three add up to `sponsors`.
   */
  counts?: { sponsors: number; approved: number; pendingReview: number; outstanding: number };
  uploadsEnabled?: boolean;
  /** The client's own auto-archive window. Null when it was not loaded, in
   *  which case the help text says no number rather than guessing one. */
  archiveAfterDays?: number | null;
  /**
   * When reminders for this event actually send, resolved server-side from the
   * client's override and the platform default.
   *
   * REPORTED HERE, SET ELSEWHERE. It is a per-client setting, so a control on
   * this screen would be a second place to set one thing and the stale one
   * would be believed. Null when it was not loaded.
   */
  reminderWindow?: { sendHour: number; skipWeekends: boolean } | null;
}

/**
 * The chaser's fixed schedule, as a person reads it.
 *
 * THE OFFSETS ARE NOT CONFIGURABLE and this list is a description of
 * lib/chaserSchedule.ts, not a source of truth for it. If those constants
 * change, this changes with them; making them editable per event was
 * considered and deliberately left out, because a reminder schedule that
 * differs per event is one nobody can reason about.
 */
const CHASER_STEPS: { when: string; what: string; mark?: 'start' | 'end' }[] = [
  { when: 'On invite', what: 'Welcome and upload request', mark: 'start' },
  { when: '3 weeks out', what: 'Early nudge' },
  { when: '7 days out', what: 'Gentle reminder' },
  { when: '3 days out', what: 'Reminder' },
  { when: '1 day out', what: 'Final notice' },
  { when: 'Deadline', what: 'Last call', mark: 'end' },
];

/** 14 as "2:00 pm". The stored value is an hour of the day, and reading a bare
 *  number back as a time is work nobody should have to do. */
function formatHour(hour: number): string {
  const h = hour % 12 === 0 ? 12 : hour % 12;
  return `${h}:00 ${hour < 12 ? 'am' : 'pm'}`;
}

type FieldErrors = Partial<Record<'endsOn' | 'deadlineDate', string>>;


/** Mirrors the server's rules for immediate feedback. The server stays
 *  authoritative and validates the merged record. */
function checkDates(startsOn: string, endsOn: string, deadlineDate: string): FieldErrors {
  const problems: FieldErrors = {};
  if (startsOn && endsOn && endsOn < startsOn) {
    problems.endsOn = 'The end date cannot be before the start date.';
  }
  if (startsOn && deadlineDate && deadlineDate >= startsOn) {
    problems.deadlineDate = 'The logo deadline must be before the event starts.';
  }
  return problems;
}

export default function EventSetup({
  clientId,
  event,
  onSaved,
}: {
  clientId: string;
  event: EventData;
  onSaved: () => void;
}) {
  const [name, setName] = useState(event.name);
  const [status, setStatus] = useState(event.status);
  const [startsOn, setStartsOn] = useState(event.startsOn ?? '');
  const [endsOn, setEndsOn] = useState(event.endsOn ?? '');
  const [deadlineDate, setDeadlineDate] = useState(event.deadline?.date ?? '');
  const [deadlineTime, setDeadlineTime] = useState(event.deadline?.time ?? '17:00');
  const [notes, setNotes] = useState(event.notesForSponsors ?? '');
  const [maxSize, setMaxSize] = useState(String(event.maxFileSizeMb));
  const [minWidth, setMinWidth] = useState(String(event.minRasterWidthPx));
  const [grace, setGrace] = useState(String(event.graceWindowDays));
  const [variations, setVariations] = useState(event.variations);

  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const [fields, setFields] = useState<FieldErrors>({});

  const live = checkDates(startsOn, endsOn, deadlineDate);
  const shown: FieldErrors = { ...live, ...fields };
  const blocked = Object.keys(live).length > 0;

  function toggleVariation(orientation: string, treatment: string) {
    const exists = variations.some((v) => v.orientation === orientation && v.treatment === treatment);
    setVariations((prev) =>
      exists
        ? prev.filter((v) => !(v.orientation === orientation && v.treatment === treatment))
        : [...prev, { orientation, treatment }],
    );
  }

  async function submit(e: FormEvent) {
    e.preventDefault();
    const problems = checkDates(startsOn, endsOn, deadlineDate);
    if (Object.keys(problems).length > 0) {
      setFields(problems);
      return;
    }

    setSaving(true);
    setProblem(null);
    setSaved(false);
    setFields({});

    const res = await fetch(`/api/clients/${clientId}/events/${event.id}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        name: name.trim(),
        status,
        startsOn: startsOn || null,
        endsOn: endsOn || null,
        deadlineDate: deadlineDate || null,
        deadlineTime,
        notesForSponsors: notes.trim() || null,
        maxFileSizeMb: Number(maxSize),
        minRasterWidthPx: Number(minWidth),
        graceWindowDays: Number(grace),
        variations,
      }),
    });

    setSaving(false);

    if (res.ok) {
      setSaved(true);
      onSaved();
      return;
    }

    const body = (await res.json().catch(() => ({}))) as { error?: string; fields?: FieldErrors };
    if (body.fields) setFields(body.fields);
    setProblem(body.error ?? 'That did not save.');
  }

  return (
    <form onSubmit={submit}>
      <div className="panel">
        <div className="panel-head"><h3><T k="eventSetup.details.title" d="Event Details" /></h3></div>
        <div className="panel-body">
          <div className="fg">
            <label className="fl" htmlFor="name"><T k="eventSetup.field.name" d="Event Name" /></label>
            <input id="name" className="fi" required value={name} onChange={(e) => setName(e.target.value)} />
          </div>

          <div className="fg">
            <label className="fl" htmlFor="status"><T k="eventSetup.field.status" d="Status" /></label>
            <select
              id="status"
              className="fi"
              style={{ maxWidth: 240 }}
              value={status}
              onChange={(e) => setStatus(e.target.value as EventData['status'])}
            >
              <option value="draft">Draft</option>
              <option value="active">Active</option>
              <option value="complete">Complete</option>
              <option value="archived">Archived</option>
            </select>
            {/* The window is a per-client setting, so the number has to come
                from the client rather than being written into the sentence. A
                client set to 90 days was previously still told "about 30", and
                one set to Never was told it would archive itself. */}
            <p className="fdesc">
              Draft: sponsors cannot see it yet. Active: collecting logos. Complete: everything is
              approved. Archived: read-only
              {typeof event.archiveAfterDays !== 'number'
                ? ', and applies automatically on the schedule set for this client.'
                : event.archiveAfterDays === 0
                  ? '. This client has auto-archiving turned off, so it stays where you leave it.'
                  : `, and auto-applies ${event.archiveAfterDays} days after the event date.`}
            </p>
          </div>

          <div className="frow2">
            <div className="fg">
              <label className="fl" htmlFor="startsOn"><T k="eventSetup.field.startsOn" d="Event Starts" /></label>
              <input id="startsOn" type="date" className="fi" value={startsOn} onChange={(e) => setStartsOn(e.target.value)} />
            </div>
            <div className="fg">
              <label className="fl" htmlFor="endsOn"><T k="eventSetup.field.endsOn" d="Event Ends" /></label>
              <input
                id="endsOn"
                type="date"
                className={`fi${shown.endsOn ? ' fi--bad' : ''}`}
                min={startsOn || undefined}
                value={endsOn}
                onChange={(e) => setEndsOn(e.target.value)}
              />
              {shown.endsOn && <p className="ferror">{shown.endsOn}</p>}
            </div>
          </div>
          {/* These are the conference dates, not the upload window, which is a
              genuinely easy thing to misread sitting right above a field
              called Logo Deadline. */}
          <p className="fdesc" style={{ marginTop: -4 }}>
            The conference dates. When sponsors can upload is set by the logo deadline below, plus
            its grace window.
          </p>

          <div className="fg">
            <label className="fl" htmlFor="deadlineDate"><T k="eventSetup.field.deadline" d="Logo Deadline" /></label>
            <div className="fetch-row" style={{ maxWidth: 340 }}>
              <input
                id="deadlineDate"
                type="date"
                className={`fi${shown.deadlineDate ? ' fi--bad' : ''}`}
                value={deadlineDate}
                onChange={(e) => setDeadlineDate(e.target.value)}
              />
              <input
                type="time"
                className="fi"
                style={{ maxWidth: 120 }}
                aria-label="Deadline time"
                value={deadlineTime}
                onChange={(e) => setDeadlineTime(e.target.value)}
              />
            </div>
            {shown.deadlineDate ? (
              <p className="ferror">{shown.deadlineDate}</p>
            ) : (
              <p className="fdesc"><T k="eventSetup.field.deadlineHint" d="Chaser reminders are anchored to this date, in Eastern Time." /></p>
            )}
          </div>
        </div>
      </div>


      {/* Its own panel, named for what it is.
       *
       * This used to be the last field inside "Logo specifications", below a
       * nine-chip grid — a sentence written to a person, filed under machine
       * constraints, and off screen when the panel opened. */}
      <div className="panel">
        <div className="panel-head">
          <h3><T k="eventSetup.told.title" d="What Sponsors Are Told" /></h3>
          <span className="hint">their invitation and their upload page</span>
        </div>
        <div className="panel-body">
          <div className="fg">
            <label className="fl" htmlFor="notes"><T k="eventSetup.field.notes" d="Notes For Sponsors" /></label>
            <textarea
              id="notes"
              className="fi ft"
              rows={3}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Please submit your primary logo in vector format, with text converted to outlines."
            />
            <p className="fdesc">
              Anything specific this event needs. Leave it empty and the message reads perfectly
              well without it.
            </p>
          </div>

          {/* Answers the question you have while writing it: who reads this,
              and where does it turn up. */}
          <p className="fl" style={{ marginTop: 'var(--sp4)' }}><T k="eventSetup.told.whereTitle" d="Where This Appears" /></p>
          <div className="whereto">
            <div className="whereto-card">
              <div className="whereto-head"><T k="eventSetup.told.invitation" d="Their Invitation" /></div>
              <div className="whereto-body">
                <p>Thank you for sponsoring {name || 'this event'} …</p>
                {notes.trim() ? <p className="whereto-hi">{notes.trim()}</p> : <p className="whereto-none"><T k="eventSetup.told.emptyInvitation" d="Your note would appear here." /></p>}
                <p>
                  {deadlineDate ? `Please send it by ${deadlineDate}.` : 'Send it whenever you are ready.'}
                </p>
              </div>
            </div>
            <div className="whereto-card">
              <div className="whereto-head"><T k="eventSetup.told.uploadPage" d="Their Upload Page" /></div>
              <div className="whereto-body">
                <p><b><T k="eventSetup.told.previewHeading" d="A note from the client" /></b></p>
                {notes.trim() ? <p className="whereto-hi">{notes.trim()}</p> : <p className="whereto-none"><T k="eventSetup.told.emptyUpload" d="Nothing shown when empty." /></p>}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="panel">
        <div className="panel-head">
          <h3><T k="eventSetup.specs.title" d="Logo Specifications" /></h3>
          <span className="hint">file limits and the versions you need</span>
        </div>
        <div className="panel-body">
          <div className="frow2">
            <div className="fg">
              <label className="fl" htmlFor="maxSize"><T k="eventSetup.field.maxSize" d="Max File Size" /></label>
              <select id="maxSize" className="fi" value={maxSize} onChange={(e) => setMaxSize(e.target.value)}>
                <option value="25">25 MB</option>
                <option value="10">10 MB</option>
                <option value="50">50 MB</option>
              </select>
            </div>
            <div className="fg">
              <label className="fl" htmlFor="minWidth"><T k="eventSetup.field.minWidth" d="Min Raster Width" /></label>
              <select id="minWidth" className="fi" value={minWidth} onChange={(e) => setMinWidth(e.target.value)}>
                <option value="1500">1500 px</option>
                <option value="1200">1200 px</option>
                <option value="2000">2000 px</option>
              </select>
              <p className="fdesc"><T k="eventSetup.field.minWidthHint" d="Only applies when a sponsor has no vector to send." /></p>
            </div>
          </div>

          <div className="fg">
            <label className="fl"><T k="eventSetup.field.versions" d="Versions Requested" /></label>
            <p className="fdesc" style={{ marginTop: 0, marginBottom: 10 }}>
              Drives each sponsor's upload checklist and the review screen. Anything they send
              outside this list is filed as an Extra.
            </p>
            {/* A grid rather than a flat chip list: orientation and treatment are
                two independent axes, and pairing them explicitly is what the
                sponsor portal and review slots key off. */}
            <div className="vgrid">
              {ORIENTATIONS.map((o) => (
                <div key={o} className="vgrid-row">
                  <span className="vgrid-label">{o}</span>
                  <div className="chips">
                    {TREATMENTS.map((t) => {
                      const on = variations.some((v) => v.orientation === o && v.treatment === t);
                      return (
                        <button
                          key={t}
                          type="button"
                          className={`chip${on ? ' on' : ''}`}
                          aria-pressed={on}
                          onClick={() => toggleVariation(o, t)}
                        >
                          {t}
                        </button>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          </div>

        </div>
      </div>

      <div className="panel">
        <div className="panel-head">
          <h3><T k="eventSetup.chaser.title" d="Automated Chaser" /></h3>
          {/* The hint here used to read "not sending yet", which stopped being
              true once the chaser job and Postmark went live. A panel that
              claims a feature is off while it is emailing sponsors hourly is
              worse than one with no hint at all. */}
          <span className="hint">running hourly</span>
        </div>
        <div className="panel-body">
          <p className="fdesc" style={{ marginTop: 0 }}>
            Reminders are anchored to the deadline and only fire on dates after a sponsor is
            invited. Chasing pauses on upload and stops once every requested version is approved,
            then keeps nudging for anything still outstanding. All dates run on Eastern Time.
          </p>

          {/* WHAT each step is, not only when. The steps were six bare dates,
              which says a schedule exists without saying what lands on a
              sponsor's desk at each point — the thing somebody setting up an
              event actually wants to check. Matches the prototype. */}
          <ol className="chaser">
            {CHASER_STEPS.map((step) => (
              <li key={step.when} className="cstep">
                <span className={`cdot${step.mark ? ` cdot--${step.mark}` : ''}`}>
                  {step.mark === 'start' ? (
                    <svg viewBox="0 0 24 24" aria-hidden="true">
                      <path d="M22 2 11 13M22 2l-7 20-4-9-9-4 20-7z" />
                    </svg>
                  ) : step.mark === 'end' ? (
                    <svg viewBox="0 0 24 24" aria-hidden="true">
                      <path d="M4 22V4h13l-2 4 2 4H4" />
                    </svg>
                  ) : (
                    <svg viewBox="0 0 24 24" aria-hidden="true">
                      <circle cx="12" cy="12" r="10" />
                      <path d="M12 6v6l4 2" />
                    </svg>
                  )}
                </span>
                <span className="cwhen">{step.when}</span>
                <span className="cwhat">{step.what}</span>
              </li>
            ))}
          </ol>

          <div className="fg" style={{ maxWidth: 320, marginTop: 'var(--sp5)' }}>
            <label className="fl" htmlFor="grace"><T k="eventSetup.field.grace" d="Grace Window After Deadline" /></label>
            <select id="grace" className="fi" value={grace} onChange={(e) => setGrace(e.target.value)}>
              <option value="0">None</option>
              <option value="2">2 days</option>
              <option value="5">5 days</option>
              <option value="10">10 days</option>
              <option value="14">14 days</option>
            </select>
            <p className="fdesc">How long a sponsor's upload link keeps working after the deadline.</p>
          </div>

          {/* THE SEND WINDOW, REPORTED RATHER THAN SET. The prototype puts a
              control here, and it was moved to the client record when the
              reminder timing became a per-client override with a platform
              default behind it. Two controls for one setting is how a screen
              comes to show a time the job will not honour, so this states what
              will actually happen and says where to change it.

              Resolved through the same function the job uses, so it cannot
              drift from the send it is describing. */}
          {event.reminderWindow && (
            <div className="fg" style={{ maxWidth: 420, marginTop: 'var(--sp4)' }}>
              <span className="fl"><T k="eventSetup.chaser.sendWindow" d="Send Window" /></span>
              <p className="chaser-window">
                <b>{formatHour(event.reminderWindow.sendHour)}</b>
                {event.reminderWindow.skipWeekends ? ', weekdays only' : ', any day'}
                {', in each event\u2019s own timezone'}
              </p>
              <p className="fdesc">
                Set per client, on the Clients screen, with a platform default behind it. It is
                shown here rather than set here so there is only one place it can be changed.
              </p>
            </div>
          )}
        </div>
      </div>

      {problem && <p className="auth-error">{problem}</p>}
      {saved && !problem && <p className="save-note"><T k="common.saved" d="Saved." /></p>}

      <div className="factions">
        <button type="submit" className="btn-compact" disabled={saving || blocked}>
          {saving ? 'Saving…' : 'Save Event'}
        </button>
      </div>
    </form>
  );
}

